import { getObjectPage } from '../utils/hasputils.js';

/**
 * Export the currently selected object as a reusable template (array of config lines
 * with coordinates normalized so the top-left of the bounding box is (0, 0)).
 *
 * @param {import('../draw/HaspObjectLayer').default} layer
 * @returns {object[]|null}
 */
export function exportSelectionAsTemplate(layer) {
    if (!layer.haspobject || layer.haspobject.isType('page')) return null;
    const rawLines = [];
    layer.haspobject.export(undefined, rawLines);
    if (!rawLines.length) return null;

    // Connections are page-level siblings rather than descendants of the
    // selected widget tree. Include only connections whose two badge
    // endpoints belong to the selected tree.
    const widgetIds = new Set(rawLines
        .filter(line => line.obj !== 'connection')
        .map(line => String(line.id)));
    const selectionPage = getObjectPage(layer.haspobject);
    const endpointTemplateIds = new Map();
    (layer.find?.(node => node.obj === 'badge') ?? [])
        .filter(node => getObjectPage(node) === selectionPage && widgetIds.has(String(node.haspid)))
        .forEach(node => {
            endpointTemplateIds.set(String(node.id()), node.haspid);
            // Accept already numeric endpoint references as well as the
            // normal Konva UUID references used by live connections.
            endpointTemplateIds.set(String(node.haspid), node.haspid);
        });

    (layer.find?.(node => node.obj === 'connection') ?? [])
        .filter(connection => getObjectPage(connection) === selectionPage)
        .forEach(connection => {
            const from = endpointTemplateIds.get(String(connection.from));
            const to = endpointTemplateIds.get(String(connection.to));
            if (from === undefined || to === undefined) return;

            const connectionLines = [];
            connection.export(undefined, connectionLines);
            connectionLines.forEach(line => {
                // Store template IDs here. Component placement maps these to
                // the fresh Konva IDs of the copied widgets.
                line.from = from;
                line.to = to;
                rawLines.push(line);
            });
        });

    const templateIds = new Set(rawLines.map(l => l.id));
    const originX = rawLines[0].x ?? 0;
    const originY = rawLines[0].y ?? 0;

    // JSON round-trip strips Konva getter/setter functions that can leak
    // when the wire-key loop does `this[prop]` on a Konva accessor name.
    const safeLines = JSON.parse(JSON.stringify(rawLines));

    return safeLines.map(line => {
        // Child objects (parentid in this template) have parent-relative coords —
        // don't subtract origin or the offset would corrupt their position.
        const isChild = line.parentid != null && templateIds.has(line.parentid);
        if (!isChild) {
            if (line.x !== undefined) line.x = line.x - originX;
            if (line.y !== undefined) line.y = line.y - originY;
        }
        delete line.custom_component_id;
        delete line.custom_component_name;
        delete line.page;
        return line;
    });
}

/**
 * Render the currently selected object to a square PNG thumbnail (data URL).
 *
 * @param {import('../draw/HaspObjectLayer').default} layer
 * @param {number} targetSize  Side length in pixels (default 128)
 * @returns {Promise<string|null>}
 */
export function exportSelectionThumbnail(layer, targetSize = 128) {
    if (!layer.haspobject || layer.haspobject.isType('page')) return Promise.resolve(null);

    const w = layer.haspobject.width();
    const h = layer.haspobject.height();
    if (!w || !h) return Promise.resolve(null);

    const maxDim = Math.max(w, h);
    const scale  = targetSize / maxDim;

    const rawDataUrl = layer.haspobject.toDataURL({ pixelRatio: scale });

    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas    = document.createElement('canvas');
            canvas.width    = targetSize;
            canvas.height   = targetSize;
            const ctx       = canvas.getContext('2d');
            const scaledW   = Math.round(w * scale);
            const scaledH   = Math.round(h * scale);
            const offsetX   = Math.floor((targetSize - scaledW) / 2);
            const offsetY   = Math.floor((targetSize - scaledH) / 2);
            ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = rawDataUrl;
    });
}
